import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';
import api from '../axios/api';
import '../assets/NewFiles/Css/SignupPage.css';
import { EyeOff, Eye, User, Mail, Smartphone, Lock, Building, Globe, Briefcase } from 'lucide-react';

const schema = yup.object({
  name: yup.string().required('Full Name is required'),
  email: yup.string().email('Invalid email address').nullable().transform((value, originalValue) => originalValue.trim() === '' ? null : value),
  phone: yup.string()
    .matches(/^[0-9]{10}$/, { message: 'Mobile number must be 10 digits', excludeEmptyString: true })
    .nullable()
    .transform((value, originalValue) => originalValue.trim() === '' ? null : value),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
  enrollAs: yup.string().required('Please select enrollment type'),
  organization: yup.string().when('enrollAs', {
    is: 'employee',
    then: (schema) => schema.required('Organization is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  industryType: yup.string().when('enrollAs', {
    is: (val) => val === 'corporate' || val === 'ngo',
    then: (schema) => schema.required('Industry type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  companyName: yup.string().when('enrollAs', {
    is: (val) => val === 'corporate' || val === 'ngo',
    then: (schema) => schema.required('Company name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  website: yup.string().url('Please enter a valid URL').nullable().transform((value, originalValue) => originalValue.trim() === '' ? null : value),
}).test('at-least-one-contact', 'Either Email or Mobile Number is required', function (values) {
  const { email, phone } = values || {};
  if (!email && !phone) {
    return this.createError({
      path: 'email',
      message: 'Either Email or Mobile Number is required',
    });
  }
  return true;
});

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [enrollAsOptions, setEnrollAsOptions] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      phone: '',
    }
  });

  const enrollAs = watch('enrollAs');

  useEffect(() => {
    const fetchEnrollAsOptions = async () => {
      try {
        const response = await api.get('/api/user/roles');
        setEnrollAsOptions(response.data[0] || []);
      } catch (error) {
        console.error('Error fetching Enroll As options:', error);
      }
    };
    fetchEnrollAsOptions();
  }, []);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (enrollAs === 'employee') {
        try {
          const response = await api.get('/api/user/organizations');
          setOrganizationOptions(response.data[0] || []);
        } catch (error) {
          console.error('Error fetching Organization Data:', error);
        }
      }
    };
    fetchOrganizationData();
  }, [enrollAs]);

  useEffect(() => {
    const fetchIndustryTypes = async () => {
      if (enrollAs === 'corporate' || enrollAs === 'ngo') {
        try {
          const response = await api.get('/api/user/industrytypes');
          setIndustryTypeOptions(response.data[0] || []);
        } catch (error) {
          console.error('Error fetching Industry Types:', error);
        }
      }
    };
    fetchIndustryTypes();
  }, [enrollAs]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const userEmail = data.email ? data.email.toLowerCase().trim() : null;
      const phoneNumber = data.phone ? data.phone.trim() : null;
      const userName = data.name;

      let selectedCompanyId = null;
      if (data.enrollAs === 'employee' && data.organization) {
        const selectedOrg = organizationOptions.find(org => org.id.toString() === data.organization.toString());
        selectedCompanyId = selectedOrg ? selectedOrg.id : null;
      }

      const signupData = {
        userEmail,
        phoneNumber,
        password: data.password,
        userName,
        enrollAs: data.enrollAs,
        companyName: data.enrollAs === 'employee' ?
          organizationOptions.find(org => org.id.toString() === data.organization.toString())?.company_name :
          data.companyName,
        website: data.website || '',
        industryType: data.industryType,
        companyId: selectedCompanyId
      };

      await api.post('/api/auth/signup', signupData);

      ReactGA.event({
        category: 'User',
        action: 'Signup Click',
        label: 'Signup Form',
      });

      toast.success('Signup successful! You can now log in.');

      setTimeout(() => {
        navigate('/login');
      }, 1800);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Signup failed. Please check your inputs.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <h2>Create Your Account</h2>
          <p>Join Aranyasetu to track and contribute to green initiatives</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid">
            {/* Full Name */}
            <div className="signup-form-group full-width">
              <label>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <User className="input-icon" size={18} />
                <input type="text" {...register('name')} placeholder="John Doe" />
              </div>
              {errors.name && <p className="error-text">{errors.name.message}</p>}
            </div>

            {/* Email Address */}
            <div className="signup-form-group">
              <label>Email Address <span style={{ color: '#ef4444' }}>*</span> <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 'normal' }}>(At least one required)</span></label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={18} />
                <input type="email" {...register('email')} placeholder="john@example.com" />
              </div>
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            {/* Mobile Number */}
            <div className="signup-form-group">
              <label>Mobile Number <span style={{ color: '#ef4444' }}>*</span> <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 'normal' }}>(At least one required)</span></label>
              <div className="input-wrapper">
                <Smartphone className="input-icon" size={18} />
                <span className="phone-prefix">+91</span>
                <input
                  type="tel"
                  maxLength={10}
                  className="with-prefix"
                  {...register('phone')}
                  placeholder="9876543210"
                />
              </div>
              {errors.phone && <p className="error-text">{errors.phone.message}</p>}
            </div>


            {/* Password */}
            <div className="signup-form-group">
              <label>Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  {...register('password')}
                  placeholder="Minimum 6 characters"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password.message}</p>}
            </div>

            {/* Confirm Password */}
            <div className="signup-form-group">
              <label>Confirm Password <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type="password"
                  {...register('confirmPassword')}
                  placeholder="Re-enter password"
                />
              </div>
              {errors.confirmPassword && <p className="error-text">{errors.confirmPassword.message}</p>}
            </div>

            {/* Enroll As */}
            <div className="signup-form-group full-width">
              <label>Enroll As <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <Briefcase className="input-icon" size={18} />
                <select {...register('enrollAs')}>
                  <option value="">Select Account Type</option>
                  {enrollAsOptions.map((option) => (
                    <option key={option.id} value={option.role_code}>{option.role_name}</option>
                  ))}
                </select>
              </div>
              {errors.enrollAs && <p className="error-text">{errors.enrollAs.message}</p>}
            </div>

            {/* Employee Organization Selector */}
            {enrollAs === 'employee' && (
              <div className="signup-form-group full-width">
                <label>Select Organization <span style={{ color: '#ef4444' }}>*</span></label>
                <div className="input-wrapper">
                  <Building className="input-icon" size={18} />
                  <select {...register('organization')}>
                    <option value="">Select Organization</option>
                    {organizationOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.company_name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.organization && <p className="error-text">{errors.organization.message}</p>}
              </div>
            )}

            {/* Corporate/NGO Specific Fields */}
            {(enrollAs === 'corporate' || enrollAs === 'ngo') && (
              <>
                <div className="signup-form-group">
                  <label>Company / Organization Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Building className="input-icon" size={18} />
                    <input type="text" {...register('companyName')} placeholder="Enter company name" />
                  </div>
                  {errors.companyName && <p className="error-text">{errors.companyName.message}</p>}
                </div>

                <div className="signup-form-group">
                  <label>Industry Type <span style={{ color: '#ef4444' }}>*</span></label>
                  <div className="input-wrapper">
                    <Briefcase className="input-icon" size={18} />
                    <select {...register('industryType')}>
                      <option value="">Select Industry Type</option>
                      {industryTypeOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.type_name}</option>
                      ))}
                    </select>
                  </div>
                  {errors.industryType && <p className="error-text">{errors.industryType.message}</p>}
                </div>

                <div className="signup-form-group full-width">
                  <label>Website</label>
                  <div className="input-wrapper">
                    <Globe className="input-icon" size={18} />
                    <input {...register('website')} placeholder="https://example.com" />
                  </div>
                  {errors.website && <p className="error-text">{errors.website.message}</p>}
                </div>
              </>
            )}
          </div>

          <button type="submit" className="signup-submit-btn" disabled={isLoading}>
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="signup-footer-link">
          Already have an account? <Link to="/login" className="auth-link">Sign In</Link>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SignupPage;